import SwapVertIcon from "@mui/icons-material/SwapVert";
import {
    Avatar,
    Box,
    Button,
    Card, CardContent,
    CircularProgress,
    Divider,
    Grid, IconButton,
    MenuItem,
    TextField,
    Typography
} from "@mui/material";
import {
    useChainModal
} from '@rainbow-me/rainbowkit';
import { ethers } from "ethers";
import { useState } from "react";
import { useAccount } from "wagmi";
import Layout from "./layouts/main";
import { bridgeAddresses, ChainEnum, destinationChainId } from "./shared/bridgeAddresses";
import useNotification from "./utilities/notificationUtils";
import { readContract, waitForTransactionReceipt, writeContract } from '@wagmi/core';
import { wagmiConfig } from "./wagmiConfig";
import { bridgeContractConfig, tokenContractConfig } from "./config";
import { Options } from "@layerzerolabs/lz-v2-utilities";
import { useNavigate } from "react-router-dom";
import { handleMetaMaskError } from "./utilities/handleMetaMaskError";

interface Token {
    address: string;
    networkId: number;
    name: string;
    symbol: string;
    logoURL: string;
    chain: ChainEnum
}

const tokens: Token[] = [
    // {
    //     address: "0x912f5f8e5d8c92f5eaa0165615e46a20882e0491",
    //     networkId: 17000,
    //     name: "Smoovie Phone",
    //     symbol: "$SP",
    //     logoURL: "https://icons.defined.fi/networks/sanko.png",
    //     chain: ChainEnum.Sanko
    // },
    // {
    //     address: "0x241a254cbf2899d4d71d0f521952972e8b5280fa",
    //     networkId: 11155111,
    //     name: "Clutch",
    //     symbol: "$CLUTCH",
    //     logoURL: "https://raw.githubusercontent.com/faisalsaleemdev/storage/main/clutch.jpeg",
    //     chain: ChainEnum.Arbitrum
    // }
    {
        address: "0x3f710cBd0F4268719C6c2E5E078a4CAfAeED7d45",
        networkId: 1996,
        name: "Smoovie Phone",
        symbol: "$SP",
        logoURL: "https://icons.defined.fi/networks/sanko.png",
        chain: ChainEnum.Sanko
    },
    {
        address: "0xF4AcDE4D938844751f34659C67056f7e69dBE85a",
        networkId: 42161,
        name: "Clutch",
        symbol: "$CLUTCH",
        logoURL: "https://raw.githubusercontent.com/faisalsaleemdev/storage/main/clutch.jpeg",
        chain: ChainEnum.Arbitrum
    }
];

export default function Bridge() {
    const { chainId } = useAccount();
    const [fromToken, setFromToken] = useState<Token>(tokens[0]);
    const [toToken, setToToken] = useState<Token>(tokens[1]);
    const [amount, setAmount] = useState("");
    const [loading, setLoading] = useState(false);
    const { showError } = useNotification();
    const { openChainModal } = useChainModal();
    const navigate = useNavigate();

    const approveToken = async (address: any, bridgeAddress: any, amount: bigint) => {
        try {
            const tx = await writeContract(wagmiConfig, {
                address: address,
                abi: tokenContractConfig.abi,
                functionName: 'approve',
                args: [bridgeAddress, amount],
            });
            await waitForTransactionReceipt(wagmiConfig, {
                hash: tx
            });
        } catch (error) {
            throw error;
        }
    };

    const handleSwap = async () => {
        try {
            if (!amount) {
                showError("Invalid amount");
                return;
            }
            if (fromToken.networkId != chainId) {
                showError("Please switch your network");
                openChainModal && openChainModal();
                return;
            }
            setLoading(true);
            const parsedAmount = ethers.parseUnits(amount?.toString() ?? "0", tokenContractConfig.tokens.token.decimals);
            const options = Options.newOptions().addExecutorLzReceiveOption(
                1000000,
                0
            );
            console.log(bridgeAddresses[fromToken.chain]);

            const quote: any = await readContract(wagmiConfig, {
                functionName: "quote",
                address: bridgeAddresses[fromToken.chain] as any,
                abi: bridgeContractConfig.abi,
                args: [
                    destinationChainId[fromToken.chain],
                    parsedAmount,
                    options.toHex()
                ],
            });

            console.log(quote);

            await approveToken(fromToken.address, bridgeAddresses[fromToken.chain], parsedAmount);

            const tx = await writeContract(wagmiConfig, {
                address: bridgeAddresses[fromToken.chain] as any,
                abi: bridgeContractConfig.abi,
                functionName: 'bridge',
                args: [
                    destinationChainId[fromToken.chain],
                    parsedAmount,
                    options.toHex()
                ],
                value: quote.nativeFee + BigInt(2000000)
            });
            console.log("Tx", tx);

            await waitForTransactionReceipt(wagmiConfig, {
                hash: tx
            });
        } catch (error) {
            const message = handleMetaMaskError(error);
            showError(message);
        } finally {
            setLoading(false);
        }

    };

    const handleTokenChange = (event: any) => {
        const selected = tokens.find((t) => t.symbol === event.target.value);
        if (selected) {
            setFromToken(selected);
            setToToken(tokens.find((t) => t !== selected) as Token);
        }
    };

    const swapTokens = () => {
        setFromToken(toToken);
        setToToken(fromToken);
    };

    return (
        <Layout>
            <Card sx={{
                maxWidth: 450,
                margin: "auto",
                mt: 6,
                p: 1,
                textAlign: "center",
                boxShadow: 4,
                borderRadius: 3,
                bgcolor: "#1e1e2f",
                color: "#fff"
            }}>
                <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold" }}>
                        Swap $SP token (Sanko Chain) for $CLUTCH token (Arbitrum) ( 1:1 Ratio )
                    </Typography>

                    {/* From Token Selection */}
                    <TextField
                        select
                        label="From Token"
                        value={fromToken.symbol}
                        onChange={handleTokenChange}
                        variant="outlined"
                        fullWidth
                        sx={{ bgcolor: "#29293d", color: "#fff", borderRadius: 2 }}
                        InputLabelProps={{ style: { color: "#fff" } }}
                        SelectProps={{
                            renderValue: (selected) => {
                                const token = tokens.find((t) => t.symbol === selected);
                                return token ? `${token.name} (${token.symbol})` : "";
                            },
                        }}
                    >
                        {tokens.map((token) => (
                            <MenuItem key={token.symbol} value={token.symbol} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Avatar src={token.logoURL} sx={{ width: 24, height: 24 }} />
                                {token.name} ({token.symbol})
                            </MenuItem>
                        ))}
                    </TextField>

                    {/* Swap Button */}
                    <Grid container justifyContent="center" sx={{ my: 2 }}>
                        <IconButton onClick={swapTokens} sx={{ color: "#fff", bgcolor: "#29293d", borderRadius: 2 }}>
                            <SwapVertIcon fontSize="large" />
                        </IconButton>
                    </Grid>

                    {/* To Token Selection */}
                    <TextField
                        select
                        label="To Token"
                        value={toToken.symbol}
                        disabled
                        variant="outlined"
                        fullWidth
                        sx={{ bgcolor: "#29293d", color: "#fff", borderRadius: 2 }}
                        InputLabelProps={{ style: { color: "#fff" } }}
                        SelectProps={{
                            renderValue: (selected) => {
                                const token = tokens.find((t) => t.symbol === selected);
                                return token ? `${token.name} (${token.symbol})` : "";
                            },
                        }}
                    >
                        <MenuItem value={toToken.symbol} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Avatar src={toToken.logoURL} sx={{ width: 24, height: 24 }} />
                            {toToken.name} ({toToken.symbol})
                        </MenuItem>
                    </TextField>


                    {/* Amount Input */}
                    <TextField
                        fullWidth
                        type="number"
                        label="Amount"
                        variant="outlined"
                        sx={{ mt: 3, bgcolor: "#29293d", borderRadius: 2, color: "#fff", input: { color: "#fff" } }}
                        value={amount}
                        onChange={(e) => {
                            if (!(Number(e.target.value) < 0)) {
                                setAmount(e.target.value)
                            }
                        }}
                    />

                    {/* Estimated Received */}
                    <Box sx={{ marginTop: '8px' }}>
                        <Typography sx={{ fontSize: 14 }}>
                            Estimated Receive: <strong>{amount || 0} {toToken.symbol}</strong>
                        </Typography>
                    </Box>

                    <Divider sx={{ my: 2, bgcolor: "#ffffff3b" }} />

                    {/* Swap Button */}
                    <Button
                        variant="contained"
                        color="secondary"
                        fullWidth
                        sx={{ mt: 2, py: 1.5, borderRadius: 2, fontWeight: "bold", fontSize: 16 }}
                        onClick={handleSwap}
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : "Bridge"}
                    </Button>

                    <IconButton onClick={() => {
                        window.location.href = "https://layerzero.network/";
                    }}
                        sx={{ marginTop: '6px' }}>
                        <img
                            src={"/layerzero.png"}
                            alt="Network Logo"
                            style={{
                                width: "30px",
                                height: "30px",
                                borderRadius: "50%",
                                marginRight: "8px",
                            }}
                        />
                        <Typography
                            variant="h1"
                            sx={{ color: "#39FF14", fontWeight: "bold", fontSize: "1rem" }}>
                            @ Powered by LayerZero
                        </Typography>
                    </IconButton>

                </CardContent>
            </Card>
        </Layout>
    );
}
